import { daysBetween } from "@nabla/api";
import type { Coverage, Fig, SourceDto } from "@nabla/api";
import { designCopy } from "../design/copy.ts";
import { formatDate, formatDateTime, saoPaulo } from "../design/format/dates.ts";
import { listOf } from "../design/format/text.ts";
import { wordsCopy as t } from "./copy.ts";
import { sourceNamed, type Words } from "./session.ts";

export type NoteMarker = "incompleto" | "atencao";

type CoverageNote = { readonly text: string; readonly short: string; readonly marker: NoteMarker };

export function days(dates: readonly string[], words: Words): string {
  const runs: string[][] = [];
  for (const date of [...dates].sort()) {
    const run = runs.at(-1);
    const last = run?.at(-1);
    if (run && last !== undefined && daysBetween(last, date) === 1) run.push(date);
    else runs.push([date]);
  }
  const shown = (date: string) => formatDate(date, words.today);
  return listOf(runs.flatMap((run) => (run.length >= 3 ? [`${shown(run[0] ?? "")}–${shown(run.at(-1) ?? "")}`] : run.map(shown))));
}

export function stamp(at: string, words: Words): string {
  return formatDateTime(at, words.today, saoPaulo);
}

export function coverageNotes(figures: readonly Fig[], words: Words): CoverageNote[] {
  const notes = figures.flatMap((fig) => coverageNote(fig.coverage, fig.source, words) ?? []);
  return notes.filter((note, index) => notes.findIndex((other) => other.text === note.text) === index);
}

export function coverageNote(coverage: Coverage, source: SourceDto, words: Words): CoverageNote | undefined {
  if (coverage.state === "completo") return undefined;
  if (coverage.state === "parcial") {
    const list = days(coverage.partialDays, words);
    const text = t.nota.incompletos(list, coverage.partialDays.length > 1);
    return { text, short: t.nota.curta(list, designCopy.incompleto), marker: "incompleto" };
  }
  const who = sourceNamed(source, words);
  if (coverage.state === "faltando") {
    const list = days(coverage.missingDays, words);
    const n = coverage.missingDays.length;
    const text = source.kind === "meta" ? t.nota.faltandoMeta(list, n) : t.nota.faltando(who.A, list, n);
    return { text, short: t.nota.curta(list, designCopy.faltando), marker: "incompleto" };
  }
  const when = coverage.newestRead === null ? "—" : stamp(coverage.newestRead, words);
  return { text: designCopy.alerta["dados-atrasados"](who.da, when), short: t.nota.curta(when, t.nota.velho), marker: "atencao" };
}
